import {
  Alert, Box, Link, Paper, Skeleton, Typography,
} from '@mui/material';
import { useContext } from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { AppConstantsContext } from '../../../context';
import { JobType } from '../../../entities';
import PaymentOptions from '../../molecules/PaymentIcons/PaymentOptions';
import PricingCard from './PricingCard';
import usePricing from './usePricing';
import useSaleEvent from './useSaleEvent';

interface StyleProps {
  additional?: any;
  isWordpress?: boolean;
}

const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  boxesWrapper: {
    display: 'grid',
    gridTemplateColumns: '.8fr 1.5fr 1.5fr',
    gap: 24,
    padding: '20px 10px',
    [theme.breakpoints.down('md')]: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
  },
  title: {
    position: 'relative',
    textAlign: 'center',
    marginBottom: 25,
    ...(props.additional?.title ?? undefined),
    [theme.breakpoints.down('sm')]: {
      fontSize: '24px',
      padding: '0 1em',
      marginBottom: '0',
      marginTop: '1em',
    },
  },
  subtitle: {
    position: 'relative',
    ...(props.additional?.subtitle ?? undefined),
  },
  scrollable: { maxHeight: props.isWordpress ? 'none' : '100vh' },

  paper: {
    background: '#242020',
    maxHeight: props.isWordpress ? 'none' : '100vh',
    padding: '20px 25px',
    maxWidth: '80em',
    margin: 'auto auto',
    [theme.breakpoints.down('md')]: {
      padding: '8px 8px',
      margin: '0',
    },
  },
  faqContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    '& a': {
      color: 'white',
      textDecoration: 'underline',
    },
  },
  disclaimer: {
    textAlign: 'right',
    marginTop: 8,
    '& a': {
      color: '#fff',
      textDecorationColor: '#ffffff8a',
    },
  },
}));
interface PricingProps {
  wordpress?: boolean;
  transcriptionId?: string;
}
const Pricing: React.FC<PricingProps> = ({ wordpress, transcriptionId }) => {
  const { subscriptionIds, jobType } = useContext(AppConstantsContext);
  const { t } = useTranslation();

  const { saleEvent } = useSaleEvent();
  const { classes } = useStyles({ additional: saleEvent?.styles, isWordpress: wordpress });
  const { priceData, loading, error } = usePricing(
    saleEvent?.discountData?.discount,
    saleEvent?.discountData?.discountedSubscriptions,
  );

  if (loading) {
    return <Skeleton animation="wave" variant="rectangular" height={600} />;
  }

  if (error) {
    return <Alert severity="error">{t('common.error')}</Alert>;
  }

  if (priceData) {
    return (
      <div className={classes.scrollable} style={{ position: 'relative' }}>
        <Paper className={classes.paper}>
          {saleEvent?.backgroundAnimation}
          <Box>
            <div>
              <Typography className={classes.title} variant="h4" component="h2">
                {saleEvent?.title ?? t('home.pricing.choose_plan')}
              </Typography>
              <div
                className={classes.subtitle}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: saleEvent?.subtitle,
                }}
              />
            </div>
            <div className={classes.boxesWrapper}>
              {/* SINGLE TICKET OR DEMO */}
              <PricingCard
                transcriptionId={transcriptionId}
                title={wordpress || !transcriptionId ? 'Demo' : 'Single Ticket'}
                cardType={wordpress || !transcriptionId ? 'free' : 'single'}
                wordpress={wordpress}
                priceOptions={
                  wordpress || !transcriptionId ? null : [priceData[subscriptionIds.SINGLE]]
                }
                matchingSaleEvent={priceData[subscriptionIds.SINGLE].hasDiscount ? saleEvent : null}
              />
              {/* PRO / UNIVERSE CARDS */}
              {subscriptionIds.UNIVERSE_MONTHLY_SUB_ID ? (
                <>
                  <PricingCard
                    title="Pro"
                    cardType="pro"
                    highlighted
                    priceOptions={[
                      priceData[subscriptionIds.MONTHLY],
                      priceData[subscriptionIds.YEARLY],
                    ]}
                    matchingSaleEvent={
                      priceData[subscriptionIds.MONTHLY].hasDiscount
                      || priceData[subscriptionIds.YEARLY].hasDiscount
                        ? saleEvent
                        : null
                    }
                  />
                  <PricingCard
                    title="Universe"
                    cardType="universe"
                    priceOptions={[
                      priceData[subscriptionIds.UNIVERSE_MONTHLY_SUB_ID],
                      priceData[subscriptionIds.UNIVERSE_YEARLY_SUB_ID],
                    ]}
                    matchingSaleEvent={
                      priceData[subscriptionIds.UNIVERSE_MONTHLY_SUB_ID].hasDiscount
                      || priceData[subscriptionIds.UNIVERSE_YEARLY_SUB_ID].hasDiscount
                        ? saleEvent
                        : null
                    }
                  />
                </>
              ) : (
                <>
                  {/* YEARLY / MONTHLY CARDS */}
                  <PricingCard
                    title="Yearly Pro"
                    cardType="yearly"
                    highlighted
                    priceOptions={[priceData[subscriptionIds.YEARLY]]}
                    matchingSaleEvent={
                      priceData[subscriptionIds.YEARLY].hasDiscount ? saleEvent : null
                    }
                  />
                  <PricingCard
                    title="Monthly Pro"
                    cardType="monthly"
                    priceOptions={[priceData[subscriptionIds.MONTHLY]]}
                    matchingSaleEvent={
                      priceData[subscriptionIds.MONTHLY].hasDiscount ? saleEvent : null
                    }
                  />
                </>
              )}
            </div>
          </Box>
          {jobType === JobType.TranscribeStudio && (
            <Typography className={classes.disclaimer}>
              <Link href="https://klang.io/terms" target="_blank">
                *
                {t('home.pricing.fair_usage_policy')}
              </Link>
            </Typography>
          )}
          <PaymentOptions />
          {!wordpress && (
            <div className={classes.faqContainer}>
              <span>{t('home.pricing.any_questions')}</span>
              <Link
                target="_blank"
                id="btn_pricing_faq"
                href="https://klang.io/frequently-asked-questions/"
              >
                {t('home.pricing.check_faq')}
              </Link>
            </div>
          )}
        </Paper>
      </div>
    );
  }
  return null;
};

export default Pricing;
